import useConnectWallet from "@/store/wallet/useConnectWallet";
import { useCallback, useEffect, useState } from "react";

/**
 * useCheckLotteryBuff - 检查抽奖结果 Hook
 * 
 * 用于调用 Lottery 合约查询用户抽奖结果。
 * 
 * @returns {Object} 返回检查方法、结果和加载状态
 *   - checkLotteryBuff: 执行检查中奖的函数
 *   - isWinner: 是否中奖（boolean）
 *   - lotteryStatus: 抽奖状态（0=PENDING, 1=NO_WIN, 2=BUFF_WIN, 3=TOKEN_WIN）
 *   - tokenAddress: 代币地址或标记地址
 *   - checkLotteryBuffLod: 加载状态
 */
const useCheckLotteryBuff = () => {
    const [lotteryStatus, setLotteryStatus] = useState<number>(0);
    const [tokenAddress, setTokenAddress] = useState<string>("");
    // loading
    const [checkLotteryBuffLod, setLoading] = useState(false);
    // 获取合约实例和地址
    const { contractEvm: { Lottery }, address } = useConnectWallet();

    /**
     * 检查是否中奖
     * @returns 返回抽奖结果 { status, tokenAddress, isWinner }
     */
    const checkLotteryBuff = useCallback(async () => {
        setLoading(true);
        if (Lottery) {
            try {
                // 先获取用户的抽奖ID计数器
                const counter = await Lottery.getUserLotteryIdCounter(address);
                // 如果计数器为0，说明用户从未参与过抽奖。
                if (counter === BigInt(0)) {
                    setLotteryStatus(0);
                    setTokenAddress("");
                    setLoading(false);
                    return { status: 0, tokenAddress: "", isWinner: false };
                }

                // 获取最新一次抽奖的结果（lotteryId = counter - 1）
                const lotteryId = counter - BigInt(1);
                const result = await Lottery.getUserLotteryResult(address, lotteryId);

                const status = Number(result.status);
                const token = result.tokenAddress;

                // 保存结果
                setLotteryStatus(status);
                setTokenAddress(token);
                // status: 0=PENDING, 1=NO_WIN, 2=BUFF_WIN, 3=TOKEN_WIN
                const winner = status === 2 || status === 3; // BUFF_WIN 或 TOKEN_WIN
                setLoading(false);
                return { status, tokenAddress: token, isWinner: winner };
            } catch (e) {
                console.log("useCheckLotteryBuff", e);
                setLoading(false);
                return { status: 0, tokenAddress: "", isWinner: false };
            }
        }
        setLoading(false);
        return { status: 0, tokenAddress: "", isWinner: false };
    }, [Lottery, address]);

    useEffect(() => {
        checkLotteryBuff()
    }, [checkLotteryBuff])

    return { checkLotteryBuff, lotteryStatus, tokenAddress, checkLotteryBuffLod };
};

export default useCheckLotteryBuff;
